// src/app/components/SphereProperties/SpherePropertiesMolecule.tsx

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@center-pwa/store/store';
import SphereTypeAvatar from '../SphereTypeAvatar/SphereTypeAvatar';
import SphereVisualControlMolecule from '../SphereVisualControl/SphereVisualControlMolecule';
import { useSphereVisual, type SphereVisualSchemaType } from '../../../hooks/useSphereVisual';
import type { SphereOwnGlyph } from 'types/glyph';
import SphereHeaderRowMolecule from '../SphereHeaderRow/SphereHeaderRowMolecule';
import SphereDescriptionEditor from './SphereDescriptionEditor';
import { SphereIdentityProvider } from '../../organisms/SphereEditor/identity/SphereIdentityContext';
import ArchetypesFieldMolecule from '../ArchetypesField/ArchetypesFieldMolecule';
import ExpectedAspectsSummaryMolecule from '../ExpectedAspectsSummary/ExpectedAspectsSummaryMolecule';
import { useSphereArchetypes } from '../../../hooks/useSphereArchetypes';
import { useExpectedAspectSetup } from '../../../hooks/useExpectedAspectSetup';
import UrgencyIcon from '../../atoms/urgency/urgency';
import SphereChatPanel from '../SphereChatPanel/SphereChatPanel';
import RelationshipGridMolecule from '../RelationshipGrid/RelationshipGridMolecule';
import PeriodDisplayMolecule from '../PeriodDisplay/PeriodDisplayMolecule';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import SphereTypeSelector from '../SphereTypeSelector/SphereTypeSelector';
import { SchemaItemResult, filterHiddenPropertiesExcluding, getTranslation } from '@center-platform/spheres/identity';
import { hasObservedValue } from '@center-platform/spheres/validation';
import './SphereProperties.scss';
import { useFormikContext, Field } from 'formik';
import { Schema, SchemaProperty } from 'types/schema';
import type { RenderMode } from 'types/aspect';
import { Sphere } from 'types/global';
import SectionGroupBodyMolecule from './SectionGroupBodyMolecule';
import { groupPropertiesByAspect } from '../../../utils/groupPropertiesByAspect';
import { HEADER_RENDERED_PROPERTY_NAMES, resolveSectionLayout, type SectionLayoutGroup } from '../../../utils/resolveSectionLayout';
import { computeWriteBlockedProps } from '../../../utils/computeWriteBlockedProps';
import { SPHERE_SECTION_SLOTS, type SphereSectionSlot } from '../../organisms/SphereEditor/sphereSectionSlots';
import { useSectionSlotDataSources } from '../../organisms/SphereEditor/useSectionSlotDataSources';
import CollapsibleSectionAtom from '../../atoms/CollapsibleSection/CollapsibleSectionAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import ArcStageSelectorMolecule from '../../molecules/ArcStageSelector/ArcStageSelector';
import ArcSummaryMolecule from '../../molecules/ArcSummary/ArcSummaryMolecule';
import AttachmentsGridMolecule from '../AttachmentsGrid/AttachmentsGridMolecule';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { urgencyLevels } from '../../../constants';
import UrgencyPillMolecule from '../../molecules/UrgencyPill/UrgencyPill';
import { useTranslation } from 'localization';
import {
  fetchArcTemplate,
  selectArcTemplate,
} from '@center-pwa/reducers/arcTemplatesReducer';

interface PermissionList {
  permissions: {
    [key: string]: boolean;
  };
}

interface SpherePropertiesMoleculeProps {
  sphere: Sphere;
  schema?: Schema;
  editable?: boolean;
  parentSphereType?: string;
  onDockedPropertyEdit?: (propertyName: string) => void;
  organizationId?: string;
  relationships?: any[];
  periodRelationships?: any[];
  relationshipsLoading?: boolean;
  relationshipsError?: string | null;
  onRelationshipsChange?: () => void;
  onShowPeriodSelector?: () => void;
  permissionList?: PermissionList;
  onNavigateToArc?: (arcId: string) => void;
  /**
   * Property names whose write permission is denied. Rendered as disabled with a lock icon. When
   * absent the molecule computes it from `permissionList`, which blocks every write-gated property
   * when that is absent too.
   */
  writeBlockedProps?: Set<string>;
  /** Content a later feature renders at the top of a schema section, keyed by section `system_name`. */
  sectionSlots?: Readonly<Record<string, SphereSectionSlot>>;
  /**
   * Called with the type picked for a new sphere, after `properties.type` is set. The owner resolves
   * that type's schema and passes it back as `schema`. Without it a new sphere offers no type picker.
   */
  onTypeChange?: (type: Schema) => void;
  /** Revision counter to trigger refresh of expected aspects after save */
  savedRevision?: number;
  /** Called when expected aspect's Show is clicked with a section ID to open/focus */
  onOpenSectionTab?: (sectionId: string) => void;
  /**
   * Called with the sphere's id when the visual control has changed what the server stores for it.
   * The control writes straight to the sphere, never through the form, so this is how the views
   * behind the editor learn to refresh their copy.
   */
  onSphereChanged?: (sphereId: string) => void;
}

interface SphereEditorFormValues {
  properties: {
    [key: string]: any;
  };
  assignedPerspectives?: any[];
}

// Helper component: Privacy lock icon (extracted to avoid duplication)
interface PrivacyLockIconProps {
  editable?: boolean;
  isPrivate: boolean;
  hasPermission: boolean;
  onToggle: () => void;
  fontSize?: string;
}

const PrivacyLockIcon: React.FC<PrivacyLockIconProps> = ({
  editable,
  isPrivate,
  hasPermission,
  onToggle,
  fontSize = '1rem'
}) => {
  const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (!hasPermission) return;
    onToggle();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle(e);
    }
  };

  if (editable) {
    const label = isPrivate ? 'Private sphere - click to make visible' : 'Public sphere - click to make private';
    return (
      <i
        className={`pi ${isPrivate ? 'pi-lock' : 'pi-lock-open'} privacy-lock-icon`}
        style={{
          marginRight: '8px',
          color: 'var(--primary-color, #3B82F6)',
          cursor: hasPermission ? 'pointer' : 'not-allowed',
          opacity: hasPermission ? 1 : 0.5,
          fontSize
        }}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={hasPermission ? 0 : -1}
        aria-label={label}
        title={label}
      />
    );
  }

  if (isPrivate) {
    return <i className="pi pi-lock privacy-lock-icon" style={{ marginRight: '8px', color: 'var(--primary-color, #3B82F6)' }} />;
  }

  return null;
}

/**
 * The form's values with the sphere's own glyph as the visual control last saved it. The control
 * writes the glyph straight to the sphere, never through the form, so the form alone would keep
 * the glyph the sphere was opened with.
 */
function withOwnGlyph(values: Record<string, unknown>, own: SphereOwnGlyph | null): Record<string, unknown> {
  const next = { ...values };
  delete next['glyph'];
  delete next['glyph_kind'];
  delete next['glyph_color'];
  if (own?.glyph) {
    next['glyph'] = own.glyph;
    next['glyph_kind'] = own.glyphKind;
  }
  if (own?.glyphColor) next['glyph_color'] = own.glyphColor;
  return next;
}

// Helper component: Mobile header with compact two-row layout
interface MobileHeaderProps {
  sphere: Sphere;
  schemaType: Schema;
  editable?: boolean;
  urgencyValue: number;
  sphereFlow: Sphere | null;
  isUrgencyPillOpen: boolean;
  setIsUrgencyPillOpen: React.Dispatch<React.SetStateAction<boolean>>;
  toggleTypeSelectorVisibility: () => void;
  typeChangeable: boolean;
  setFieldValue: (field: string, value: any) => void;
  setSphereFlow: (flow: Sphere | null) => void;
  errors: any;
  touched: any;
  values: SphereEditorFormValues;
  t: (key: string) => string;
  onPrivacyToggle: () => void;
  hasManageSecurityPermission: boolean;
  visualControl?: React.ReactNode;
}

const MobileHeader: React.FC<MobileHeaderProps> = ({
  sphere,
  schemaType,
  editable,
  urgencyValue,
  sphereFlow,
  isUrgencyPillOpen,
  setIsUrgencyPillOpen,
  toggleTypeSelectorVisibility,
  typeChangeable,
  setFieldValue,
  setSphereFlow,
  errors,
  touched,
  values,
  t,
  onPrivacyToggle,
  hasManageSecurityPermission,
  visualControl
}) => (
  <>
    {/* Row 1: Icons + Name */}
    <div className="header-row-1">
      <div className="header-left-icons">
        {editable && typeChangeable ? (
          <SphereTypeAvatar sphere={sphere} onClick={toggleTypeSelectorVisibility} />
        ) : visualControl ?? (
          <div className="sphere-type-label">
            <SphereTypeAvatar sphere={sphere} title={schemaType?.name} editable={false} />
          </div>
        )}
        <div className="urgency-container-compact">
          {editable ? (
            <button
              type="button"
              className="urgency-button-compact"
              onClick={(e) => {
                e.stopPropagation();
                setIsUrgencyPillOpen((prev) => !prev);
              }}
              aria-label="Toggle Urgency"
            >
              <UrgencyIcon urgencyValue={urgencyValue} />
            </button>
          ) : (
            <span className="urgency-view" title={urgencyLevels[urgencyValue].label}>
              <UrgencyIcon urgencyValue={urgencyValue} />
            </span>
          )}
          {isUrgencyPillOpen && (
            <UrgencyPillMolecule
              placement="popover"
              onUrgencyChange={(urgency: string) => {
                setFieldValue('properties.urgency', urgency);
                setIsUrgencyPillOpen(false);
              }}
              urgencyValue={urgencyValue}
            />
          )}
        </div>
      </div>

      <div className="header-center-name">
        <PrivacyLockIcon
          editable={editable}
          isPrivate={values.properties?.['is_private']}
          hasPermission={hasManageSecurityPermission}
          onToggle={onPrivacyToggle}
          fontSize="1rem"
        />
        <Field
          type="text"
          id="properties.name"
          name="properties.name"
          placeholder={errors.properties?.name && touched.properties?.name && !values.properties?.name ? t('common.required') : t('sphere.placeholders.name')}
          disabled={!editable}
          className={`sphere-name-input-compact ${errors.properties?.name && touched.properties?.name ? 'is-invalid' : ''}`}
        />
      </div>
    </div>

    {/* Row 2: Flow Controls (always visible) */}
    {sphereFlow && (
      <div className="header-row-2">
        <div className="flow-components-container-mobile">
          <ArcStageSelectorMolecule
            arc={sphereFlow}
            onStageUpdated={(updatedArc) => {
              // Map transition response history → stages format
              const history = updatedArc.properties?.history || [];
              const mappedStages = history.map((h: any) => ({
                sphereId: sphere.id,
                stageInstanceId: h.stageId,
                stageDefinitionId: h.stageId,
                arcId: updatedArc.id,
                status: h.status,
                startedAt: h.startedAt || h.timestamp,
                completedAt: h.completedAt,
              }));
              setSphereFlow({
                ...updatedArc,
                stages: mappedStages,
              });
            }}
          />
        </div>
      </div>
    )}
  </>
);

// Helper component: Collapsible section wrapper for mobile
interface CollapsibleSectionProps {
  title: string;
  isExpanded: boolean;
  setIsExpanded: (value: boolean) => void;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  isExpanded,
  setIsExpanded,
  children
}) => (
  <div className="mobile-collapsible">
    <button
      type="button"
      className="collapsible-header"
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <span className="collapsible-title">{title}</span>
      <i className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'} collapsible-icon`} />
    </button>
    {isExpanded && <div className="collapsible-content">{children}</div>}
  </div>
);

// Helper function: Render period display with mobile/desktop conditional logic
const renderPeriodDisplay = (
  sphere: Sphere,
  hasAppliedToPeriodRelationship: any,
  effectiveOrganizationId: string | null | undefined,
  isMobile: boolean,
  isPeriodsExpanded: boolean,
  setIsPeriodsExpanded: (value: boolean) => void,
  t: (key: string) => string,
  editable: boolean | undefined,
  periodRelationships: any[],
  relationshipsLoading: boolean,
  relationshipsError: string | null,
  onRelationshipsChange: (() => void) | undefined,
  onShowPeriodSelector: (() => void) | undefined
) => {
  console.log('🔍 PeriodDisplay Debug - Rendering conditions:', {
    sphereId: sphere.id,
    hasAppliedToPeriodRelationship: !!hasAppliedToPeriodRelationship,
    effectiveOrganizationId: effectiveOrganizationId,
    shouldRender: !!(sphere.id && hasAppliedToPeriodRelationship && effectiveOrganizationId)
  });

  if (!sphere.id || !hasAppliedToPeriodRelationship) {
    console.log('❌ PeriodDisplay Debug - NOT rendering PeriodDisplayMolecule');
    return null;
  }

  console.log('✅ PeriodDisplay Debug - Rendering PeriodDisplayMolecule');

  const periodDisplayProps = {
    sphereId: sphere.id,
    editable,
    periodRelationships,
    relationshipsLoading,
    relationshipsError,
    onPeriodsChange: () => {
      console.log('Periods changed for sphere:', sphere.id);
      onRelationshipsChange?.();
    },
    onShowPeriodSelector
  };

  return isMobile ? (
    <CollapsibleSection
      title={t('sphere.sections.periods')}
      isExpanded={isPeriodsExpanded}
      setIsExpanded={setIsPeriodsExpanded}
    >
      <PeriodDisplayMolecule {...periodDisplayProps} collapsible={false} />
    </CollapsibleSection>
  ) : (
    <div className="sphere-periods-section">
      <PeriodDisplayMolecule {...periodDisplayProps} collapsible={true} />
    </div>
  );
};

const SpherePropertiesMolecule: React.FC<SpherePropertiesMoleculeProps> = ({
  sphere,
  schema,
  editable,
  parentSphereType,
  onDockedPropertyEdit,
  organizationId,
  relationships = [],
  periodRelationships = [],
  relationshipsLoading = false,
  relationshipsError = null,
  onRelationshipsChange,
  onShowPeriodSelector,
  permissionList,
  onNavigateToArc,
  writeBlockedProps: writeBlockedPropsProp,
  sectionSlots = SPHERE_SECTION_SLOTS,
  onTypeChange,
  savedRevision,
  onOpenSectionTab,
  onSphereChanged
}) => {
  console.log('🏠 SphereProperties - Component rendered with props:', {
    sphereId: sphere.id,
    organizationId,
    editable,
    hasSchema: !!schema,
    parentSphereType
  });

  const dispatch = useDispatch<AppDispatch>();
  const apiClients = useApiClientsOrNull();
  const storeOrganizationId = useSelector((state: RootState) => state.nodes.organizationId);
  const { t, i18n } = useTranslation();

  // Move all useState hooks before any early returns
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  // Per-section expansion, keyed by section group key; a section nobody collapsed is open.
  const [sectionExpanded, setSectionExpanded] = useState<Record<string, boolean>>({});
  const [pendingReveal, setPendingReveal] = useState<{ aspectSystemName: string; propertyName?: string } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [isUrgencyPillOpen, setIsUrgencyPillOpen] = useState(false);
  const [sphereFlow, setSphereFlow] = useState<Sphere | null>(null);
  const [showHeaderPrivacyConfirm, setShowHeaderPrivacyConfirm] = useState(false);

  const schemaType = schema ?? null;

  // Slot stores are read synchronously by `appliesTo`/`ownsProperty` during the layout resolve below
  // (not memoised here), so the hook only has to start loads and re-render this component on change.
  useSectionSlotDataSources(sectionSlots, sphere.id);

  // Detect mobile viewport
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Mobile collapsible sections state - collapsed by default on mobile for cleaner UI
  const [isAdditionalPropsExpanded, setIsAdditionalPropsExpanded] = useState(!isMobile);

  // A reveal waits for the render that expands its section: a collapsed body is `inert`, which the
  // atom lifts in its own effect, and child effects run before this one.
  useEffect(() => {
    if (!pendingReveal) return;
    const root = rootRef.current;
    if (!root) return;
    // The missing field's own row when the aspect renders per property (also inside a whole-aspect
    // fallback grid); the aspect's container when it renders as one component.
    const aspectContainer = root.querySelector<HTMLElement>(
      `[data-aspect-system-name="${pendingReveal.aspectSystemName}"]`
    );
    const propertyRow = pendingReveal.propertyName
      ? (aspectContainer ?? root).querySelector<HTMLElement>(`[data-property-name="${pendingReveal.propertyName}"]`)
      : null;
    const target = propertyRow ?? aspectContainer;
    setPendingReveal(null);
    if (!target) return;
    // A disabled control cannot take focus, so a write-blocked field falls back to its row.
    const focusable = target.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"], button:not([disabled])'
    );
    const focusTarget = focusable ?? target;
    if (!focusable) focusTarget.tabIndex = -1;
    const reduceMotion = globalThis.window?.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    focusTarget.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    focusTarget.focus({ preventScroll: true });
  }, [pendingReveal]);
  const [isPeriodsExpanded, setIsPeriodsExpanded] = useState(!isMobile);
  const [isAttachmentsExpanded, setIsAttachmentsExpanded] = useState(!isMobile);
  const [isRelationshipsExpanded, setIsRelationshipsExpanded] = useState(!isMobile);
  const [isChatExpanded, setIsChatExpanded] = useState(false);

  const { values, setFieldValue, setFieldTouched, errors, touched, submitCount } = useFormikContext<SphereEditorFormValues>();

  const urgencyValue = Number(values.properties.urgency ?? '2'); // Default urgency

  // Calculate permission for header lock icon
  const hasManageSecurityPermission = !sphere.id || (permissionList?.permissions?.manage_security ?? false);

  // Resolve center and parent names from assigned perspectives for confirmation dialog
  const resolvedCenterName = useMemo(() => {
    const centerPerspective = values.assignedPerspectives?.find(
      (ap) => ap.perspectiveProperties?.system_name === 'center_center'
    );
    return centerPerspective?.assigned?.[0]?.sphere?.properties?.name || t('sphere.privacy.unknownCenter');
  }, [values.assignedPerspectives, t]);

  const resolvedParentName = useMemo(() => {
    const parentPerspective = values.assignedPerspectives?.find(
      (ap) => ap.perspectiveProperties?.system_name === 'center_parent'
    );
    return parentPerspective?.assigned?.[0]?.sphere?.properties?.name;
  }, [values.assignedPerspectives]);

  // The bubble + confirm dialog only matter while creating a new sphere.
  // For existing spheres the privacy state is already in effect; explaining it
  // here would be redundant, and toggling needs a real confirm dialog
  // (handled elsewhere via the property write path).
  const isNewSphere = !sphere.id;

  // An existing sphere's type cannot change (the backend refuses it), so only a new sphere gets
  // the type selector; Archetypes are how an existing sphere says more about what it is.
  const archetypesState = useSphereArchetypes(isNewSphere ? undefined : sphere.id, {
    refreshKey: savedRevision
  });
  // A picture change moves the `avatar` slot the setup view evaluates, with no property save to follow it.
  const [visualRevision, setVisualRevision] = useState(0);
  const savedSphereId = isNewSphere ? undefined : (sphere.id as string);
  const visualSchemaType = useMemo<SphereVisualSchemaType>(
    () => ({
      system_name: schemaType?.system_name ?? '',
      glyph: schemaType?.glyph,
      glyph_color: schemaType?.glyph_color,
    }),
    [schemaType]
  );
  // One visual state for the sphere: the header and the Identity row both render it, so a change
  // made in either shows in both.
  const visual = useSphereVisual(
    { id: savedSphereId ?? '', properties: sphere.properties as Record<string, unknown> | undefined },
    visualSchemaType,
    { onSphereChanged }
  );
  const evaluatedValues = useMemo(() => {
    const formValues = values.properties || {};
    return isNewSphere ? formValues : withOwnGlyph(formValues, visual.own);
  }, [isNewSphere, values.properties, visual.own]);
  const expectedAspectsSetup = useExpectedAspectSetup({
    sphereId: savedSphereId,
    expectedAspects: archetypesState.expectedAspects,
    evaluation: archetypesState.expectedAspectsEvaluation,
    archetypes: archetypesState.archetypes,
    schema,
    values: evaluatedValues,
    refreshKey: `${savedRevision ?? 0}:${visualRevision}`
  });
  const typeChangeable = isNewSphere && !!onTypeChange;
  // Interactive only when the server said `edit` is granted: an unresolved or failed permission
  // check leaves the list without `edit`, so the mark stays static.
  const canChangeVisual = !!editable && permissionList?.permissions?.edit === true;
  // The header and the Identity row are two controls over the one `visual` state; each keeps only
  // its own panel, dialogs and toast.
  const renderVisualControl = (variant: 'header' | 'row', labelledBy?: string) =>
    savedSphereId && schemaType ? (
      <SphereVisualControlMolecule
        visual={visual}
        sphereId={savedSphereId}
        sphereName={(values.properties?.name as string | undefined) || undefined}
        sphereType={visualSchemaType}
        sphereTypeLabel={schemaType.name}
        readOnly={!canChangeVisual}
        variant={variant}
        labelledBy={labelledBy}
        onSaveComplete={() => setVisualRevision((r) => r + 1)}
      />
    ) : undefined;
  const headerVisualControl = renderVisualControl('header');
  // The read-only reason names the one person who can act only when the SERVER says the authority
  // is theirs alone (`assignGate: 'self'` — a person sphere with a login, gated by
  // `subject.assign_archetypes`). The sphere IS that person, so its own name is the name. For an
  // `edit`-gated sphere there is no single owner to name and the generic copy stands; guessing from
  // the label alone would tell a reader "only X can change this" about a sphere several people can.
  const archetypesOwnerName =
    archetypesState.assignGate === 'self' ? (values.properties?.['name'] as string | undefined) || undefined : undefined;

  const headerArchetypes = useMemo(
    () =>
      archetypesState.status === 'ready'
        ? archetypesState.archetypes.map((a) => ({
            key: a.systemName,
            label: a.displayName,
            isPrimary: a.isPrimary,
            unavailable: !a.resolved,
          }))
        : undefined,
    [archetypesState.status, archetypesState.archetypes]
  );

  // Dynamic helper text based on privacy state — future-tense for new spheres.
  const privacyHelperText = useMemo(() => {
    const isPrivate = values.properties?.['is_private'];
    if (isPrivate) {
      return t('sphere.privacy.helperTextOnNew', { centerName: resolvedCenterName });
    } else if (resolvedParentName) {
      return t('sphere.privacy.helperTextOffWithParentNew', {
        centerName: resolvedCenterName,
        parentName: resolvedParentName,
      });
    } else {
      return t('sphere.privacy.helperTextOffNoParentNew', { centerName: resolvedCenterName });
    }
  }, [values.properties, resolvedCenterName, resolvedParentName, t]);

  // Handler for privacy toggle from header lock icon
  const handleHeaderPrivacyToggle = () => {
    const isCurrentlyPrivate = values.properties?.['is_private'];

    if (isCurrentlyPrivate && !isNewSphere) {
      // Unlocking an existing sphere — confirmation required.
      setShowHeaderPrivacyConfirm(true);
    } else {
      // New sphere (toggling pre-save) or locking — no confirmation needed.
      setFieldValue('properties.is_private', !isCurrentlyPrivate);
    }
  };

  const handleConfirmHeaderPrivacyChange = () => {
    setFieldValue('properties.is_private', false);
    setShowHeaderPrivacyConfirm(false);
  };

  const handleCancelHeaderPrivacyChange = () => {
    setShowHeaderPrivacyConfirm(false);
  };

  // Get template name from sphereFlow (will be set after fetching flow)
  let templateSystemName: string | null = null;
  if (sphereFlow) {
    const rawValue = sphereFlow.properties.templateSystemName;
    templateSystemName = Array.isArray(rawValue) ? rawValue[0] : rawValue;
  }

  // Get flow template from Redux store (hook must be called unconditionally)
  const flowTemplate = useSelector((state: RootState) =>
    templateSystemName ? selectArcTemplate(state, templateSystemName) : null
  );

  // Resolve organizationId - prefer prop, fallback to store
  const effectiveOrganizationId = organizationId || storeOrganizationId;

  console.log('🔍 SphereProperties Debug - OrganizationId resolution:', {
    propOrganizationId: organizationId,
    storeOrganizationId,
    effectiveOrganizationId
  });
  
  // Check if conversations are enabled for this schema type
  // If conversations_enabled is not specified, default to true (backward compatible)
  const conversationsEnabled = (schemaType as any)?.conversations_enabled !== false;


  const toggleTypeSelectorVisibility = () => {
    if (!typeChangeable) return;
    setShowTypeSelector((prev) => !prev);
  };

  const handleTypeSelect = (result: SchemaItemResult) => {
    void setFieldValue('properties.type', result.schema.system_name);
    onTypeChange?.(result.schema);
    setShowTypeSelector(false);
  };

  // Derive arc sphere from sphere.arcs[] — no separate fetch needed
  useEffect(() => {
    const primaryArc = sphere.arcs?.find(a => a.isPrimary) || sphere.arcs?.[0];
    if (primaryArc) {
      // Map StageStatus[] → StageInstance[] format expected by ArcStageSelectorMolecule
      const mappedStages = (primaryArc.stages || []).map((s: any) => ({
        sphereId: sphere.id,
        stageInstanceId: s.stageId || s.stageInstanceId,
        stageDefinitionId: s.stageId || s.stageDefinitionId,
        arcId: primaryArc.id,
        status: s.status,
        startedAt: s.startedAt,
        completedAt: s.completedAt,
      }));

      setSphereFlow({
        id: primaryArc.id,
        label: 'arc',
        properties: {
          type: 'arc',
          name: primaryArc.templateSystemName,
          templateSystemName: primaryArc.templateSystemName,
          templateVersion: primaryArc.templateVersion,
          currentStageId: primaryArc.currentStageId,
          stageEnteredAt: primaryArc.stageEnteredAt,
        },
        stages: mappedStages,
      });
    } else {
      setSphereFlow(null);
    }
  }, [sphere.arcs, sphere.id]);

  // Fetch flow template from Redux when sphereFlow exists (will use cache if available)
  useEffect(() => {
    if (sphereFlow && apiClients?.schemaClient && templateSystemName) {
      // Only fetch if not already cached
      if (!flowTemplate) {
        dispatch(fetchArcTemplate({ systemName: templateSystemName, apiClient: apiClients.schemaClient }));
      }
    }
  }, [sphereFlow, apiClients, templateSystemName, flowTemplate, dispatch]);

  // Determine if multi-arc summary should be shown (2+ arcs)
  const showArcSummary = (sphere.arcs?.length ?? 0) >= 2;

  // Find most urgent arc action for header promotion
  const urgentArcAction = useMemo(() => {
    if (!showArcSummary || !sphere.arcs) return null;
    const priorityOrder = ['overdue', 'blocked', 'at_risk', 'attention_needed'];
    for (const status of priorityOrder) {
      const arc = sphere.arcs.find(a => a.health?.status === status);
      if (arc) return { arc, status };
    }
    return null;
  }, [showArcSummary, sphere.arcs]);

  // Early returns AFTER all hooks
  if (!apiClients) {
    return null;
  }

  if (!schemaType) {
    return (
      <EmptyStateAtom
        icon="pi-exclamation-triangle"
        title={t('sphere.schemaLoadError.title')}
        description={t('sphere.schemaLoadError.description')}
        data-testid="sphere-schema-load-error"
      />
    );
  }

  // is_private is excluded — its dedicated UI is the lock icon in the header.
  // It became a real schema property in the property-access-controls migration,
  // so without this exclusion it'd auto-render as a generic switch in the grid.
  const additionalProperties = filterHiddenPropertiesExcluding(schemaType.properties, HEADER_RENDERED_PROPERTY_NAMES);

  // Whole-aspect grouping (EAINT-855 SC1): pull a contributed aspect's properties out of the flat
  // list and mount its declared whole-aspect component once, in place of that group's rows — only
  // when the aspect's `components.aspect` map actually resolves for this mode; otherwise the
  // group's properties fall back to the ordinary flattened rendering below.
  const mode: RenderMode = editable ? 'edit' : 'view';
  const writeBlockedProps = writeBlockedPropsProp ?? computeWriteBlockedProps(schemaType.properties, permissionList);
  const { segments } = groupPropertiesByAspect(additionalProperties, schemaType.aspectComponents, mode, values.properties ?? {}, writeBlockedProps);

  const sectionLayout = resolveSectionLayout({
    sections: schemaType.sections,
    properties: additionalProperties,
    slots: sectionSlots,
    slotContext: { sphere, schemaType, mode, editable: !!editable },
    writeBlockedProps,
    aspectComponents: schemaType.aspectComponents,
  });

  const sectionTitleOf = (group: SectionLayoutGroup): string =>
    group.section
      ? getTranslation(i18n, group.section.label_key, group.section.label ?? group.key)
      : t('sphere.sections.details');

  // Show on a setup-view row: open the section that holds the aspect (a docked section is its own
  // tab, which the editor opens), then focus the first field still missing in it.
  const revealAspect = (aspectSystemName: string, groupKey: string) => {
    expectedAspectsSetup.markRevealed(aspectSystemName);
    if (sectionLayout.docked.some((g) => g.key === groupKey)) {
      onOpenSectionTab?.(groupKey);
      return;
    }
    // Target the first rendered property the evaluation covered (never a masked one) that has no value.
    const hidden = new Set((schema?.properties ?? []).filter((p) => p.hidden).map((p) => p.name));
    const evaluated = (
      expectedAspectsSetup.rows.find((r) => r.aspectSystemName === aspectSystemName)?.evaluatedProperties ?? []
    ).filter((name) => !hidden.has(name));
    const target = evaluated.find((name) => !hasObservedValue(values.properties?.[name])) ?? evaluated[0];
    setSectionExpanded((prev) => ({ ...prev, [groupKey]: true }));
    setIsAdditionalPropsExpanded(true);
    setPendingReveal({ aspectSystemName, propertyName: target });
  };

  // Check if this schema has the applies_to_period relationship
  const hasAppliedToPeriodRelationship = useMemo(() => {
    console.log('🔍 PeriodDisplay Debug - Schema relationships check:', {
      schemaType: schemaType?.system_name,
      hasSchemaType: !!schemaType,
      relationships: schemaType?.relationships,
      relationshipCount: schemaType?.relationships?.length || 0,
      relationshipSystemNames: schemaType?.relationships?.map((rel: any) => rel.system_name) || []
    });
    
    const foundRelationship = schemaType?.relationships?.find(
      (rel: any) => rel.system_name === 'applies_to_period'
    );
    
    console.log('🎯 PeriodDisplay Debug - applies_to_period relationship found:', !!foundRelationship, foundRelationship);
    
    return foundRelationship;
  }, [schemaType]);

  // The identity slot renders the description and archetypes itself; without it (an injected
  // registry that lacks `identity`) the standalone blocks below keep rendering them.
  const identitySlotRenders = [...sectionLayout.inline, ...sectionLayout.docked].some(
    (group) => group.key === 'identity' && !!group.slot
  );
  const showExpectedAspectsSummary =
    !isNewSphere && !showTypeSelector && (archetypesState.status === 'loading' || archetypesState.expectedAspects.length > 0);
  const expectedAspectsSummary = showExpectedAspectsSummary ? (
    <ExpectedAspectsSummaryMolecule
            sphereId={sphere.id}
            expectedAspects={archetypesState.expectedAspects}
            groups={expectedAspectsSetup.groups}
            needsAttentionCount={expectedAspectsSetup.needsAttentionCount}
            evaluation={expectedAspectsSetup.evaluation}
            isLoading={archetypesState.status === 'loading' && archetypesState.expectedAspects.length === 0}
            typeName={schemaType.name}
            groupTitle={(groupKey) => {
              const group = [...sectionLayout.inline, ...sectionLayout.docked].find((g) => g.key === groupKey);
              return group ? sectionTitleOf(group) : null;
            }}
            onReveal={revealAspect}
            onRetry={() => archetypesState.refetch()}
            isRetrying={archetypesState.refreshing}
          />
  ) : null;
  const identityContextValue = {
    archetypesState,
    archetypesOwnerName,
    isNewSphere,
    editable: !!editable,
    renderVisualRow: headerVisualControl ? (labelledBy: string) => renderVisualControl('row', labelledBy) : undefined,
    expectedAspectsSummary,
  };

  return (
    <SphereIdentityProvider value={identityContextValue}>
    <div className="sphere-properties-molecule" ref={rootRef}>
      {/* Header Bar - Compact Two Rows on Mobile */}
      {isMobile ? (
        <div className="sphere-properties-header mobile-compact">
          <MobileHeader
            sphere={sphere}
            schemaType={schemaType}
            editable={editable}
            urgencyValue={urgencyValue}
            sphereFlow={sphereFlow}
            isUrgencyPillOpen={isUrgencyPillOpen}
            setIsUrgencyPillOpen={setIsUrgencyPillOpen}
            toggleTypeSelectorVisibility={toggleTypeSelectorVisibility}
            typeChangeable={typeChangeable}
            setFieldValue={setFieldValue}
            setSphereFlow={setSphereFlow}
            errors={errors}
            touched={touched}
            values={values}
            t={t}
            onPrivacyToggle={handleHeaderPrivacyToggle}
            hasManageSecurityPermission={hasManageSecurityPermission}
            visualControl={headerVisualControl}
          />
        </div>
      ) : (
        <div className="sphere-properties-header-row">
          <SphereHeaderRowMolecule
            sphere={sphere}
            editable={editable}
            typeTitle={schemaType?.name}
            onTypeClick={toggleTypeSelectorVisibility}
            typeChangeable={typeChangeable}
            archetypes={headerArchetypes}
            name={values.properties?.name ?? ''}
            onNameChange={(v) => setFieldValue('properties.name', v)}
            onNameBlur={() => setFieldTouched('properties.name', true)}
            namePlaceholder={
              errors.properties?.name && touched.properties?.name && !values.properties?.name
                ? t('common.required')
                : t('sphere.placeholders.name')
            }
            nameInvalid={!!(errors.properties?.name && touched.properties?.name)}
            showUrgency
            urgencyValue={urgencyValue}
            onUrgencyChange={(u) => setFieldValue('properties.urgency', String(u))}
            showPrivacy
            isPrivate={!!values.properties?.['is_private']}
            onPrivacyToggle={handleHeaderPrivacyToggle}
            hasManageSecurityPermission={hasManageSecurityPermission}
            visualControl={headerVisualControl}
          >
            {sphereFlow && (
              <div className="flow-components-container">
                <ArcStageSelectorMolecule
                  arc={sphereFlow}
                  onStageUpdated={(updatedFlow) => {
                    setSphereFlow(updatedFlow);
                  }}
                />
              </div>
            )}
          </SphereHeaderRowMolecule>
        </div>
      )}

      {!identitySlotRenders && !isNewSphere && !showTypeSelector && (
        <ArchetypesFieldMolecule archetypes={archetypesState} readOnlyOwnerName={archetypesOwnerName} />
      )}

      {!identitySlotRenders && expectedAspectsSummary}

      {/* Privacy Information Banner — only shown while creating a new sphere */}
      {!showTypeSelector && isNewSphere && (() => {
        const isPrivate = values.properties?.['is_private'];
        const containerClass = isPrivate
          ? 'privacy-sphere-container'
          : 'privacy-sphere-container privacy-sphere-container--warn';
        const iconClass = isPrivate
          ? 'pi pi-lock privacy-sphere-icon'
          : 'pi pi-exclamation-triangle privacy-sphere-icon';
        return (
          <div className={containerClass}>
            <i className={iconClass} aria-hidden="true" />
            <p className="privacy-sphere-helper-text">{privacyHelperText}</p>
          </div>
        );
      })()}

      {/* Primary Arc Action Promotion - shown when multi-arc sphere has urgent action */}
      {urgentArcAction && onNavigateToArc && (
        <div
          className={`arc-action-promotion arc-action-promotion--${urgentArcAction.status.replace('_', '-')}`}
          role="button"
          tabIndex={0}
          onClick={() => onNavigateToArc(urgentArcAction.arc.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onNavigateToArc(urgentArcAction.arc.id);
            }
          }}
        >
          <i className="pi pi-exclamation-triangle arc-action-promotion__icon" />
          <span className="arc-action-promotion__text">
            {(() => {
              let statusKey: string = urgentArcAction.status;
              if (urgentArcAction.status === 'at_risk') statusKey = 'atRisk';
              else if (urgentArcAction.status === 'attention_needed') statusKey = 'attentionNeeded';
              return t(`arcSummary.action.${statusKey}`);
            })()}
            {' — '}
            {urgentArcAction.arc.templateSystemName}
          </span>
          <span className="arc-action-promotion__link">{t('arcSummary.viewArcs')}</span>
        </div>
      )}

      {/* Type Selector */}
      {showTypeSelector && parentSphereType && (
        <SphereTypeSelector
          sphereSystemName={parentSphereType}
          perspectiveSystemNames={['center_parent', 'center_center']}
          onTypeSelect={handleTypeSelect}
          selectedType={schemaType.system_name}
          sphereId={sphere.id}
        />
      )}

      {/* Standalone description: only when the identity slot is not rendering it */}
      {!identitySlotRenders && !showTypeSelector && <SphereDescriptionEditor editable={editable} />}

      {/*
        Additional properties: whole-aspect renderers (SC1) followed by the remaining flattened
        rows. Both are the SAME section — an aspect group is a replacement for its contributed
        properties' rows, not a separate region — so on mobile they share one CollapsibleSection.
        Hoisting the groups above it would leave them permanently expanded over a collapsed
        "Additional properties" header holding their siblings.
      */}
      {(sectionLayout.onlyDefault ? segments.length > 0 : sectionLayout.inline.length > 0) &&
        (() => {
          const renderBody = (properties: SchemaProperty[], group?: SectionLayoutGroup) => (
            <SectionGroupBodyMolecule
              properties={properties}
              schemaType={schemaType}
              editable={!!editable}
              writeBlockedProps={writeBlockedProps}
              onDockedPropertyEdit={onDockedPropertyEdit}
              slot={group?.slot}
              slotContext={group?.slotContext}
            />
          );

          // A type with no sections keeps today's markup: one flat run, collapsible only on mobile,
          // with no section headings. Whole-aspect renderers and flattened rows are the SAME
          // section, so on mobile they share one CollapsibleSection.
          if (sectionLayout.onlyDefault) {
            const additionalPropertiesContent = renderBody(additionalProperties);
            return isMobile ? (
              <CollapsibleSection
                title={t('sphere.sections.additionalProperties')}
                isExpanded={isAdditionalPropsExpanded}
                setIsExpanded={setIsAdditionalPropsExpanded}
              >
                {additionalPropertiesContent}
              </CollapsibleSection>
            ) : (
              additionalPropertiesContent
            );
          }

          const groupHasErrors = (group: SectionLayoutGroup) =>
            group.allProperties.some((prop) => {
              const error = (errors.properties as Record<string, unknown> | undefined)?.[prop.name];
              const isTouched = (touched.properties as Record<string, unknown> | undefined)?.[prop.name];
              return !!error && (!!isTouched || submitCount > 0);
            });

          return sectionLayout.inline.map((group) => {
            const title = sectionTitleOf(group);
            const needsAttention = expectedAspectsSetup.needsAttentionBySection[group.key] ?? 0;
            return (
              <CollapsibleSectionAtom
                key={group.key}
                title={title}
                subtitle={
                  group.section?.description
                    ? getTranslation(i18n, group.section.description_key, group.section.description)
                    : undefined
                }
                hasErrors={groupHasErrors(group)}
                errorMessage={t('sphere.sections.hasErrors')}
                showErrorMessage
                expanded={sectionExpanded[group.key] ?? true}
                onExpandedChange={(next) => setSectionExpanded((prev) => ({ ...prev, [group.key]: next }))}
                titleAdornment={
                  needsAttention > 0 ? (
                    <TagAtom
                      tone="warn"
                      size="small"
                      icon="pi-exclamation-circle"
                      label={t('archetypes.expected.needsAttention', { count: needsAttention })}
                      ariaLabel={t('archetypes.expected.sectionIndicatorLabel', { count: needsAttention, section: title })}
                      data-testid={`sphere-section-indicator-${group.key}`}
                    />
                  ) : undefined
                }
                anchorId={`sphere-section-${group.key}`}
                data-testid={`sphere-section-${group.key}`}
              >
                {renderBody(group.properties, group)}
              </CollapsibleSectionAtom>
            );
          });
        })()}

      {/* Period Display - Show for spheres with applies_to_period relationship */}
      {renderPeriodDisplay(
        sphere,
        hasAppliedToPeriodRelationship,
        effectiveOrganizationId,
        isMobile,
        isPeriodsExpanded,
        setIsPeriodsExpanded,
        t,
        editable,
        periodRelationships,
        relationshipsLoading,
        relationshipsError,
        onRelationshipsChange,
        onShowPeriodSelector
      )}

      {/* Attachments Grid - Schema-config gated; hidden when explicitly disabled */}
      {sphere.id && (schemaType as Schema & { attachments?: { enabled?: boolean } })?.attachments?.enabled !== false && (
        isMobile ? (
          <CollapsibleSection
            title={t('attachments.sectionTitle')}
            isExpanded={isAttachmentsExpanded}
            setIsExpanded={setIsAttachmentsExpanded}
          >
            <AttachmentsGridMolecule
              sphereId={sphere.id}
              canEdit={editable ?? false}
            />
          </CollapsibleSection>
        ) : (
          <div className="sphere-attachments-section">
            <AttachmentsGridMolecule
              sphereId={sphere.id}
              canEdit={editable ?? false}
            />
          </div>
        )
      )}

      {/* Relationship Grid - Show for existing spheres in both view and edit modes */}
      {sphere.id && (
        isMobile ? (
          <CollapsibleSection
            title={t('sphere.sections.relationships')}
            isExpanded={isRelationshipsExpanded}
            setIsExpanded={setIsRelationshipsExpanded}
          >
            <RelationshipGridMolecule
              sphereId={sphere.id}
              sphere={sphere}
              editable={editable}
              organizationId={organizationId}
              collapsible={false}
              relationships={relationships}
              relationshipsLoading={relationshipsLoading}
              relationshipsError={relationshipsError}
              onRelationshipsChange={onRelationshipsChange}
              onShowPeriodSelector={onShowPeriodSelector}
            />
          </CollapsibleSection>
        ) : (
          <div className="sphere-relationships-section">
            <RelationshipGridMolecule
              sphereId={sphere.id}
              sphere={sphere}
              editable={editable}
              organizationId={organizationId}
              collapsible={true}
              relationships={relationships}
              relationshipsLoading={relationshipsLoading}
              relationshipsError={relationshipsError}
              onRelationshipsChange={onRelationshipsChange}
              onShowPeriodSelector={onShowPeriodSelector}
            />
          </div>
        )
      )}

      {/* Arc Summary Table - shown when sphere has 2+ arcs */}
      {showArcSummary && sphere.arcs && (
        <ArcSummaryMolecule
          arcs={sphere.arcs}
          onNavigateToArc={onNavigateToArc}
        />
      )}

      {/* Chat Panel */}
      {!showTypeSelector && sphere.id && conversationsEnabled && (
        isMobile ? (
          <CollapsibleSection
            title={t('sphere.sections.chatComments')}
            isExpanded={isChatExpanded}
            setIsExpanded={setIsChatExpanded}
          >
            <SphereChatPanel
              contextKey={`sphere:${sphere.id}`}
              collapsible={false}
              viewMode={!editable}
              showThreadContext={false}
            />
          </CollapsibleSection>
        ) : (
          <div className="sphere-chat-section">
            <SphereChatPanel
              contextKey={`sphere:${sphere.id}`}
              collapsible={true}
              viewMode={!editable}
              showThreadContext={false}
            />
          </div>
        )
      )}

      {/* Confirmation Dialog for Header Lock Icon */}
      <Dialog
        visible={showHeaderPrivacyConfirm}
        onHide={handleCancelHeaderPrivacyChange}
        header={t('sphere.privacy.confirmDialog.title')}
        className="privacy-confirm-dialog"
        style={{ width: '450px' }}
        dismissableMask
      >
        <div className="privacy-confirm-content">
          <p className="privacy-confirm-message">{t('sphere.privacy.confirmDialog.message')}</p>

          <div className="privacy-confirm-scopes">
            {resolvedCenterName && (
              <div className="privacy-confirm-scope-item">
                <i className="pi pi-building" />
                <span>
                  <strong>{t('sphere.privacy.confirmDialog.center')}:</strong> {resolvedCenterName}
                </span>
              </div>
            )}

            {resolvedParentName && (
              <div className="privacy-confirm-scope-item">
                <i className="pi pi-folder" />
                <span>
                  <strong>{t('sphere.privacy.confirmDialog.parent')}:</strong> {resolvedParentName}
                </span>
              </div>
            )}
          </div>

          <p className="privacy-confirm-details">{t('sphere.privacy.confirmDialog.details')}</p>
        </div>

        <div className="privacy-confirm-actions">
          <Button
            label={t('sphere.privacy.confirmDialog.makeVisible')}
            onClick={handleConfirmHeaderPrivacyChange}
            className="p-button-outlined p-button-danger"
          />
          <Button
            label={t('sphere.privacy.confirmDialog.keepPrivate')}
            onClick={handleCancelHeaderPrivacyChange}
            className="p-button"
            autoFocus
          />
        </div>
      </Dialog>
    </div>
    </SphereIdentityProvider>
  );
};

export default SpherePropertiesMolecule;
